import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsAccordion } from 'components/common/ns-accordion/NsAccordion';
import { NsExpander } from 'components/common/ns-expander/NsExpander';
import { NsList } from 'components/common/ns-list/NsList';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';

export const testId = 'solarFAQTestId';

export const SolarFAQ = () => {
  return (
    <NsPanel testId={testId}>
      <NsAccordion heading="More about Hive Solar Saver" headingTag="h3">
        <NsExpander
          heading="Do I need to install additional hardware?"
          HeadingTag="h4"
        >
          <p>
            No, our Hive Solar Saver has no impact on your solar or battery
            installation.
          </p>
        </NsExpander>
        <NsExpander
          heading="What if I want to change my solar or battery settings?"
          HeadingTag="h4"
        >
          <p>
            Hive Solar Saver has no impact on your battery settings. You can
            change your settings or schedule through your Hive app.
          </p>
        </NsExpander>
        <NsExpander
          heading="Do I need to be a British Gas customer?"
          HeadingTag="h4"
        >
          <p>
            Yes, you need to get your electricity from British Gas to sign up
            for the add-on. You can use any supplier for your gas, but we can
            also supply that for you too.
          </p>
        </NsExpander>
        <NsExpander
          heading="I’m on an EV/TOU tariff, am I eligible for Hive Solar Saver?"
          HeadingTag="h4"
        >
          <p>
            Yes, you can sign up for Hive Solar Saver if you are on a
            time-of-use tariff (e.g. Electric Driver or an Economy 7 tariff),
            however, you&apos;ll only receive the 25% discount on your
            electricity usage during the peak period. For example, if
            you&apos;re on our Electric Driver tariff you&apos;d receive the 25%
            discount on your electricity consumption between 5am and midnight
            each day.
          </p>
          <p>
            You&apos;re also eligible for Hive Solar Saver if you have Hive
            SmartCharge or FreeCharge enabled. You&apos;ll see two credits on
            your bill each month, one for your EV usage and the other for Hive
            Solar Saver.
          </p>
        </NsExpander>

        <NsExpander
          heading="What energy usage figures do I use to get a quote?"
          HeadingTag="h4"
        >
          <p>
            After installing solar panels and/or a battery, your annual
            electricity consumption will likely change. As part of your system
            quote, your installer may be able to provide an estimate of your
            expected annual electricity consumption after your solar panels
            and/or battery have been installed. Please contact your installer
            for guidance. If you use gas at your property, please refer to your
            gas bill.
          </p>
        </NsExpander>
        <NsExpander
          heading="I already have solar panels, can I apply for the Hive Solar Saver?"
          HeadingTag="h4"
        >
          <p>
            No, this offer is only available to new customers that purchase
            solar and/or battery from Hive from the 22nd May 2024.
          </p>
        </NsExpander>
        <NsExpander
          heading="My circumstances have changed, am I still eligible for the add-on?"
          HeadingTag="h4"
        >
          <p>You&apos;ll no longer be eligible if you: </p>
          <NsList>
            {[
              `Change supplier or move home`,
              `Have prepayment meters installed`,
              `Switch to a tariff on our old system`
            ]}
          </NsList>
          <p>
            If this happens we&apos;ll write to you to let you know that
            we&apos;ve removed the offer from your account. You&apos;ll receive
            any payments due before we remove the offer from your account.
            Please note, any payments due to you may be sent by cheque or bank
            transfer and take longer to process than usual.
          </p>
        </NsExpander>
      </NsAccordion>

      <NsAccordion heading="Payments" headingTag="h3">
        <NsExpander heading="When will I receive my credits?" HeadingTag="h4">
          <p>
            Your payments will be applied to your next electricity bill and will
            appear as &apos;Hive Solar Saver Credit&apos;. To change your
            billing cycle, e.g. from every 3 months to monthly, you can{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chat online
            </NsAnchor>{' '}
            with one of our friendly advisers.
          </p>
        </NsExpander>
        <NsExpander heading="How do you work out my payments?" HeadingTag="h4">
          <p>
            Your credits are equal to your electricity consumption in your
            previous billing cycle multiplied by 25%. If you&apos;re on a
            time-of-use tariff (where you&apos;re charged more than one unit
            rate), the 25% discount will only apply to your consumption during
            the peak period.
          </p>
          <p>
            We use your smart meter readings to calculate your electricity usage
            during your billing period, if for any reason we&apos;re unable to
            get your smart meter data to work out your consumption, for example,
            if your meter stops sending us readings, we may have to estimate
            your consumption and payment.
          </p>
        </NsExpander>
        <NsExpander heading="Will my Direct Debit change?" HeadingTag="h4">
          <p>
            No, your direct debit won’t be affected by your Hive Solar Saver
            credits. You can request a refund for account credits by{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chatting online
            </NsAnchor>{' '}
            to one of our advisers. If you have any credits left if you leave
            British Gas, these will be added to your final British Gas bill.
          </p>
        </NsExpander>
        <NsExpander
          heading="If I change my tariff, will my payments change?"
          HeadingTag="h4"
        >
          <p>
            Yes, if you change your tariff, we&apos;ll work out your credits
            with your new unit rate from the day your new tariff starts.
          </p>
        </NsExpander>
      </NsAccordion>

      <NsAccordion heading="Help & Support" headingTag="h3">
        <NsExpander
          heading="I haven’t received my account credit"
          HeadingTag="h4"
        >
          <p>
            Your credit will show on your next bill as &apos;Hive Solar Saver
            Credit&apos;. We aim to add your credit as part of your next billing
            cycle but sometimes it can take a little longer. If you don&apos;t
            see your credit on your next bill, you can{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chat online
            </NsAnchor>{' '}
            with one of our friendly advisers who can look into this for you.
          </p>
        </NsExpander>
        <NsExpander heading="My credit’s lower than I expected" HeadingTag="h4">
          <p>
            If you&apos;re on a time-of-use tariff (e.g. Electric Driver or an
            Economy 7 tariff) you&apos;ll only receive the 25% discount on your
            electricity usage during the peak period. For example, if
            you&apos;re on our Electric Driver tariff you&apos;d receive the 25%
            discount on your electricity consumption between 5am and midnight
            each day.
          </p>
        </NsExpander>
        <NsExpander
          heading="I no longer want the Hive Solar Saver add-on"
          HeadingTag="h4"
        >
          <p>
            To opt out of Hive Solar Saver please let us know by{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/contact-us">
              chatting online
            </NsAnchor>{' '}
            with one of our friendly advisers.
          </p>
        </NsExpander>
        <NsExpander
          heading="How do I sign up for the smart export guarantee tariff?"
          HeadingTag="h4"
        >
          <p>
            You can find further information about{' '}
            <NsAnchor href="https://www.britishgas.co.uk/energy/smart-export-guarantee.html">
              our SEG tariffs
            </NsAnchor>{' '}
            and how to apply online.
          </p>
        </NsExpander>
      </NsAccordion>
    </NsPanel>
  );
};
